'use client';

import { type Component, type ComponentProps } from '../../types/component';
import { tv } from '../../utils/tv';
import { Typography, type TypographyProps } from '../typography/Typography';
import { useMenuItemContext } from './internal/MenuItemContext';

const menuItemTextClasses = {
  root: 'zds-menu-item-text-root',
  primary: 'zds-menu-item-text-primary',
  secondary: 'zds-menu-item-text-secondary',
} as const;

const menuItemTextStyles = tv({
  slots: {
    root: [menuItemTextClasses.root, 'col-start-2', 'flex min-w-0 flex-col'],
    primary: [menuItemTextClasses.primary],
    secondary: [menuItemTextClasses.secondary],
  },
});

interface MenuItemTextBaseProps {
  /**
   * Primary text content. Supports inline elements (e.g. badges).
   */
  children?: React.ReactNode;
  /**
   * Secondary/description text.
   */
  secondary?: React.ReactNode;
  /**
   * Props applied to internal slot elements.
   *
   * @see {@link https://zeus.serko.travel/?path=/docs/zeus-core-next-components-menu-components-menu-itemtext--overview#slotprops | slotProps documentation}
   */
  slotProps?: {
    primary?: TypographyProps;
    secondary?: TypographyProps;
  };
}

type MenuItemTextProps = ComponentProps<MenuItemTextBaseProps, 'span'>;

type MenuItemTextComponent = Component<MenuItemTextBaseProps, 'span'>;

const MenuItemText: MenuItemTextComponent = (props) => {
  const { children, secondary, slotProps, className, ref, ...rest } = props;

  const itemContext = useMenuItemContext();
  const isDisabled = itemContext?.isDisabled ?? false;

  const styles = menuItemTextStyles();

  return (
    <span {...rest} ref={ref} className={styles.root({ className })}>
      {children != null && (
        <Typography
          component="span"
          variant="body2"
          {...slotProps?.primary}
          className={styles.primary({
            className: slotProps?.primary?.className,
          })}
          // Force inherit on disabled so the parent item's
          // `disabled-foreground` wins over any consumer colour.
          color={isDisabled ? 'inherit' : slotProps?.primary?.color}
        >
          {children}
        </Typography>
      )}
      {secondary != null && (
        <Typography
          component="span"
          variant="body2"
          {...slotProps?.secondary}
          className={styles.secondary({
            className: slotProps?.secondary?.className,
          })}
          color={
            isDisabled
              ? 'inherit'
              : (slotProps?.secondary?.color ?? 'secondary')
          }
        >
          {secondary}
        </Typography>
      )}
    </span>
  );
};

MenuItemText.displayName = 'Menu.ItemText';

export {
  MenuItemText,
  type MenuItemTextProps,
  type MenuItemTextBaseProps,
  menuItemTextClasses,
};
